import React, { useEffect, FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import MastercardPng from '../../../assets/images/topUpBankCard/mastercard.png';
import MastercardWebp from '../../../assets/images/topUpBankCard/mastercard.webp';
import Visa from '../../../assets/images/topUpBankCard/visa.png';
import { FormInputText } from '../../inputs/FormInputText';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { ImgWebp } from '../../commons/ImgWeb';
import { CustomTooltipInfo } from '../../commons/CustomTooltipInfo';
import { ReactComponent as InfoIcon } from '../../../assets/images/InfoOutlined.svg';
import { ButtonClassNames, InputTextFormat, ModalVariants } from '../../../models/Common';
import { usePlaton } from '../../../hooks/usePlaton';

export const TopUpBankCard: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const defaultTopUp = queryParams.has('amount') ? Number(queryParams.get('amount')) : undefined;
  const methods = useForm<{ topUpAmount: number }>();
  const { setValue, reset, handleSubmit } = methods;
  const { isPlatonError } = usePlaton();
  const { settings } = useAppSelector((state) => state.appReducer);
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const { openModal } = modalSlice.actions;
  const dispatch = useAppDispatch();
  const { data: members, isFetching: isFetchingMembers } = userInfoApi.useGetMultiaccountMembersQuery('', {
    refetchOnMountOrArgChange: true,
    skip: !settings?.feature?.multiaccount,
  });
  const { data, isLoading: isAmountLoading } = userInfoApi.useGetRecommendedTopUpAmountQuery();
  const isSkeletonDisabled = isUserInfoDownloaded && !isAmountLoading && !isFetchingMembers;

  useEffect(() => {
    if (defaultTopUp) {
      setValue('topUpAmount', defaultTopUp);
    } else if (data?.amount) {
      setValue('topUpAmount', data?.amount);
    } else {
      reset();
    }
  }, [defaultTopUp, data?.amount]);

  useEffect(() => {
    if (isUserInfoDownloaded && queryParams.has('amount') && queryParams.has('userId')) {
      const userId = queryParams.get('userId');
      if (userInfoState.id.toString() !== userId) {
        dispatch(
          openModal({
            modalVariants: ModalVariants.TopUpWarning,
            content: {
              accountId: String(userId),
            },
          }),
        );
      }
    }
  }, [isUserInfoDownloaded]);

  const handleTopUp = ({ topUpAmount }: { topUpAmount: number }) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.TopUpTypeMethods,
        content: {
          service: t('topUpBalance.personalAccountReplenishment'),
          amount: Number(topUpAmount),
          userId: queryParams.has('bannerId') ? Number(queryParams.get('bannerId')) : undefined,
        },
      }),
    );
  };

  return (
    <div className={`top-up-bank-card ${isSkeletonDisabled ? 'skeleton--disabled' : 'skeleton'}`}>
      <h1 className="title top-up-scratch-card__title">{t('topUpBalance.topUpWithPaymentCard')}</h1>
      <div className="card card__primary card__primary_full-mobile top-up-bank-card__container">
        <FormProvider {...methods}>
          <form className="top-up-bank-card__container__form" onSubmit={handleSubmit(handleTopUp)}>
            <article className="top-up-bank-card__container__form__title">{t('topUpBalance.enterTopUpAmount')}</article>
            <FormInputText
              name="topUpAmount"
              className="primary"
              label=""
              isRequired
              format={InputTextFormat.Currency}
              minValue={1}
              maxValue={99999999.99}
              maxValueErrorMessage={t('validation.amountExceedsLimit')}
              endAdornment={t('common.uah')}
            />
            {!!data?.amount && members?.Account?.Role === 'admin' ? (
              <div className="top-up-bank-card__container__form__recommended-sum">
                <div className="top-up-bank-card__container__form__recommended-sum__title">
                  {t('topUpBalance.recomendSum')}
                  <CustomTooltipInfo
                    tooltipText=""
                    element={
                      <>
                        <div style={{ maxWidth: '250px' }}>{t('topUpBalance.recomendSumInfo')}</div>
                      </>
                    }
                  >
                    <div className="top-up-bank-card__container__form__recommended-sum__tooltip">
                      <InfoIcon />
                    </div>
                  </CustomTooltipInfo>
                </div>
                <p className="top-up-bank-card__container__form__recommended-sum__value">
                  {`${data?.amount || 0} ${t('common.uah')}`}
                </p>
              </div>
            ) : null}
            <CustomMUIButton
              label={t('buttons.continue')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isDisabled={isPlatonError}
            />
          </form>
        </FormProvider>
        <div className="top-up-bank-card__container__icons">
          <ImgWebp fallback={Visa} alt="visa" />
          <ImgWebp webpSrc={MastercardWebp} fallback={MastercardPng} alt="mastercard" />
        </div>
      </div>
    </div>
  );
};
